import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef } from "preact/hooks";
import { Button } from "./Button.tsx";
import { Icon } from "./Icon.tsx";

/**
 * A side sheet over the right of the page, for help and detail that shouldn't
 * be always on. Rule: it never blocks the page, so you can read it beside what
 * it explains; Escape or the close button closes it, and focus goes back to
 * what opened it.
 */
export function Drawer({ open, title, onClose, children, footer }: {
  open: boolean; title: ComponentChildren; onClose: () => void; children: ComponentChildren; footer?: ComponentChildren;
}) {
  const sheet = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const id = useId();
  useEffect(() => {
    const dialog = sheet.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.show();
      dialog.querySelector<HTMLElement>(".ui-drawer__close")?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return <dialog ref={sheet} class="ui-drawer" aria-labelledby={id}>
    <header class="ui-drawer__head"><h2 id={id}>{title}</h2><Button icon size="sm" variant="ghost" class="ui-drawer__close" aria-label="Close" onClick={onClose}><Icon name="close" size={16} /></Button></header>
    <div class="ui-drawer__body">{children}</div>
    {footer && <footer class="ui-drawer__foot">{footer}</footer>}
  </dialog>;
}
